import React, { useEffect, useState } from "react";
import PublicLayout from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const EMPTY = { parent_name: "", email: "", phone: "", student_name: "", student_age: "", interest: "", preferred_date: "", step_up_user: "no", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [contact, setContact] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/site-content/contact_info").then((r) => setContact(r.data.value || {})).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email) { toast.error("Please share your name and email."); return; }
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, step_up_user: form.step_up_user === "yes" });
      setSent(true);
      toast.success("Your inquiry was sent. H.E.A. will be in touch soon.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <PublicLayout>
      <section className="bg-navy topo-bg">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <span className="eyebrow text-exploration">Contact</span>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-white lg:text-5xl">Start Your Next Learning Adventure.</h1>
          <p className="mt-4 max-w-xl text-white/70">Submit an inquiry about memberships or experiences. Submitting this form does not reserve a place — H.E.A. will follow up with availability and next steps.</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-16 lg:grid-cols-3 lg:px-8">
        <div className="space-y-4 lg:col-span-1">
          <h3 className="font-display text-xl font-bold text-navy">Reach H.E.A.</h3>
          {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-3 text-slateBrand hover:text-brandblue"><Mail className="h-5 w-5 text-brandblue" />{contact.email}</a>}
          {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-3 text-slateBrand hover:text-brandblue"><Phone className="h-5 w-5 text-brandblue" />{contact.phone}</a>}
          {contact.address && <div className="flex items-center gap-3 text-slateBrand"><MapPin className="h-5 w-5 text-brandblue" />{contact.address}</div>}
        </div>

        <div className="lg:col-span-2">
          {sent ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-successBrand" />
              <h2 className="mt-5 font-display text-2xl font-bold text-navy">Inquiry received</h2>
              <p className="mt-2 text-slateBrand">Thank you! H.E.A. will reach out with availability and how to finalize your registration.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <div><Label>Parent / Guardian name</Label><Input data-testid="contact-parent-name" className="mt-1.5 h-12" value={form.parent_name} onChange={set("parent_name")} required /></div>
                <div><Label>Email</Label><Input data-testid="contact-email" type="email" className="mt-1.5 h-12" value={form.email} onChange={set("email")} required /></div>
                <div><Label>Phone</Label><Input data-testid="contact-phone" className="mt-1.5 h-12" value={form.phone} onChange={set("phone")} /></div>
                <div><Label>Student name</Label><Input data-testid="contact-student-name" className="mt-1.5 h-12" value={form.student_name} onChange={set("student_name")} /></div>
                <div><Label>Student age</Label><Input data-testid="contact-student-age" className="mt-1.5 h-12" value={form.student_age} onChange={set("student_age")} /></div>
                <div><Label>Preferred experience date</Label><Input data-testid="contact-preferred-date" type="date" className="mt-1.5 h-12" value={form.preferred_date} onChange={set("preferred_date")} /></div>
                <div className="sm:col-span-2">
                  <Label>Interested membership / experience</Label>
                  <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                    <SelectTrigger data-testid="contact-interest" className="mt-1.5 h-12"><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent>
                      {["Explorer Membership", "Discoverer Membership", "Trailblazer Membership", "H.E.A. Day Explorer (individual)", "H.E.A. Signature Experience", "Not sure yet"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Are you a Step Up For Students scholarship user?</Label>
                  <RadioGroup value={form.step_up_user} onValueChange={(v) => setForm({ ...form, step_up_user: v })} className="mt-2 flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-navy" data-testid="contact-stepup-yes"><RadioGroupItem value="yes" />Yes</label>
                    <label className="flex items-center gap-2 text-sm text-navy" data-testid="contact-stepup-no"><RadioGroupItem value="no" />No</label>
                  </RadioGroup>
                </div>
                <div className="sm:col-span-2"><Label>Additional questions</Label><Textarea data-testid="contact-message" className="mt-1.5" rows={4} value={form.message} onChange={set("message")} /></div>
              </div>
              <p className="mt-4 text-xs text-slateBrand">{contact.privacy}</p>
              <Button data-testid="contact-submit" disabled={sending} className="mt-5 h-12 w-full bg-brandblue font-display text-base font-semibold hover:bg-blue-700">
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Inquiry"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
